import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import { BASE_ENDPOINT } from "../helpers/Config";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import {
  FaUsers,
  FaUserCheck,
  FaUserSlash,
  FaCoins,
  FaDollarSign,
  FaSyncAlt,
  FaChartPie,
  FaWallet,
  FaLock,
  FaArrowDown,
  FaArrowUp,
  FaChartLine,
  FaExchangeAlt,
  FaPeopleArrows,
  FaHandHoldingUsd,
  FaRandom,
  FaClock,
  FaExclamationTriangle,
  FaGift,
  FaShoppingCart,
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";

const ZERO_CA = { count: 0, amount: 0 };
const ZERO_STATUS = {
  total: { ...ZERO_CA },
  pending: { ...ZERO_CA },
  success: { ...ZERO_CA },
  rejected: { ...ZERO_CA },
};
const ZERO_EX_LEG = { count: 0, inr: 0, usdt: 0 };
const ZERO_EX = {
  total: { ...ZERO_EX_LEG },
  pending: { ...ZERO_EX_LEG },
  success: { ...ZERO_EX_LEG },
  rejected: { ...ZERO_EX_LEG },
};

const DEFAULT_DATA = {
  users: { total: 0, active: 0, inactive: 0 },
  balance: {
    coin: { total: 0, active: 0, inactive: 0, lock: 0 },
    usdt: { total: 0, active: 0, inactive: 0, lock: 0 },
  },
  deposits: { ...ZERO_STATUS },
  withdrawals: {
    total: { count: 0, amount: 0, net: 0 },
    pending: { ...ZERO_CA },
    success: { ...ZERO_CA },
    rejected: { ...ZERO_CA },
  },
  trades: {
    total: { ...ZERO_CA },
    pending: { ...ZERO_CA },
    win: { ...ZERO_CA },
    loss: { ...ZERO_CA },
    profit: 0,
  },
  exchange: {
    buy: { ...ZERO_EX },
    sell: { ...ZERO_EX },
  },
  p2p: { count: 0, amount: 0 },
  commission: {
    total_count: 0,
    pending: 0,
    claimed: 0,
    by_type: {
      refer: 0,
      deposit: 0,
      self_deposit: 0,
      trade: 0,
      ti_coin_buy: 0,
    },
  },
  ti_coin_buy: {
    self: { ...ZERO_CA },
    downline: { ...ZERO_CA },
    total: 0,
  },
  swaps: {
    usdt_to_coin: { count: 0, usdt: 0, coin: 0 },
    coin_to_usdt: { count: 0, usdt: 0, coin: 0 },
  },
  recovery: {
    total_count: 0,
    active_count: 0,
    total_amount: 0,
    pending_amount: 0,
    claimed_amount: 0,
    unclaimed_amount: 0,
  },
  today: {
    date: "",
    new_users: 0,
    deposits: {
      total: { ...ZERO_CA },
      success: { ...ZERO_CA },
      pending: { ...ZERO_CA },
    },
    withdrawals: {
      total: { ...ZERO_CA },
      success: { ...ZERO_CA },
      pending: { ...ZERO_CA },
    },
    trades: {
      total: { ...ZERO_CA },
      win: { ...ZERO_CA },
      loss: { ...ZERO_CA },
    },
  },
  pending_queue: {
    deposits: 0,
    withdrawals: 0,
    exchange_buy: 0,
    exchange_sell: 0,
    total: 0,
  },
};

function n(v) {
  const num = Number(v);
  return Number.isFinite(num) ? num : 0;
}

function nfmt(v, decimals = 0) {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n(v));
}

function money(v) {
  return nfmt(v, 2);
}

function pct(part, total) {
  const p = n(total) > 0 ? (n(part) / n(total)) * 100 : 0;
  return Math.max(0, Math.min(100, p));
}

function SkeletonLine({ w = "100%" }) {
  return <span className="tb-skeleton" style={{ width: w }} />;
}

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: Math.min(0.04 * i, 0.35), duration: 0.35, ease: [0.22, 1, 0.36, 1] },
  }),
};

const TONES = {
  teal: { bg: "rgba(15,118,110,0.12)", color: "#0f766e" },
  amber: { bg: "rgba(217,119,6,0.12)", color: "#d97706" },
  sky: { bg: "rgba(2,132,199,0.12)", color: "#0284c7" },
  rose: { bg: "rgba(220,38,38,0.12)", color: "#dc2626" },
  violet: { bg: "rgba(124,58,237,0.12)", color: "#7c3aed" },
  slate: { bg: "rgba(51,65,85,0.12)", color: "#334155" },
};

function KpiTile({ icon, label, value, hint, loading, index = 0, tone = "teal", col = "col-12 col-sm-6 col-xl-3" }) {
  const t = TONES[tone] || TONES.teal;
  return (
    <motion.div className={col} custom={index} variants={fadeUp} initial="hidden" animate="show">
      <div className="card border-0 tb-kpi-card h-100">
        <div className="card-body d-flex align-items-center gap-3" style={{ minHeight: 96 }}>
          <div className="tb-kpi-icon" style={{ background: t.bg, color: t.color }}>
            {icon}
          </div>
          <div className="flex-grow-1 min-w-0">
            <div className="text-muted small fw-semibold text-uppercase" style={{ letterSpacing: "0.04em", fontSize: "0.68rem" }}>
              {label}
            </div>
            {loading ? (
              <div className="mt-2">
                <SkeletonLine w="55%" />
              </div>
            ) : (
              <>
                <div className="h5 fw-bold mb-0 mt-1 tb-display text-truncate">{value}</div>
                {hint ? <div className="small text-muted mt-1 text-truncate">{hint}</div> : null}
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function Panel({ title, icon, right, children, index = 0, className = "" }) {
  return (
    <motion.div className={`card border-0 h-100 ${className}`} custom={index} variants={fadeUp} initial="hidden" animate="show">
      <div className="card-header pt-3 pb-2">
        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <span className="tb-panel-icon">{icon}</span>
            <div className="fw-semibold tb-display">{title}</div>
          </div>
          {right}
        </div>
      </div>
      <div className="card-body pt-2">{children}</div>
    </motion.div>
  );
}

function StatRow({ label, value, sub, tone }) {
  return (
    <div className="d-flex align-items-start justify-content-between gap-2 py-2 border-bottom" style={{ borderColor: "var(--tb-line)" }}>
      <div>
        <div className="small fw-semibold">{label}</div>
        {sub ? <div className="text-muted" style={{ fontSize: "0.72rem" }}>{sub}</div> : null}
      </div>
      <div className={`fw-bold text-end ${tone || ""}`} style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
    </div>
  );
}

function StatusGrid({ rows, loading }) {
  if (loading) {
    return (
      <>
        <SkeletonLine w="100%" />
        <div className="mt-2" />
        <SkeletonLine w="85%" />
        <div className="mt-2" />
        <SkeletonLine w="70%" />
      </>
    );
  }
  return (
    <div>
      {rows.map((r) => (
        <StatRow key={r.label} label={r.label} value={r.value} sub={r.sub} tone={r.tone} />
      ))}
    </div>
  );
}

function BalanceBlock({ title, icon, total, active, inactive, lock, loading, accent }) {
  const activePct = pct(active, total);
  return (
    <div className="tb-metric-block p-3 h-100">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center gap-2">
          <span style={{ color: accent }}>{icon}</span>
          <div className="fw-semibold">{title}</div>
        </div>
        {loading ? (
          <span className="badge text-bg-secondary">Loading</span>
        ) : (
          <span className="badge text-bg-dark">Total: {money(total)}</span>
        )}
      </div>

      {loading ? (
        <>
          <SkeletonLine w="70%" />
          <div className="mt-2" />
          <SkeletonLine w="100%" />
        </>
      ) : (
        <>
          <div className="row g-2 mt-1">
            <div className="col-4">
              <div className="text-muted small">Active</div>
              <div className="fw-bold">{money(active)}</div>
            </div>
            <div className="col-4">
              <div className="text-muted small">Inactive</div>
              <div className="fw-bold">{money(inactive)}</div>
            </div>
            <div className="col-4">
              <div className="text-muted small d-flex align-items-center gap-1">
                <FaLock style={{ fontSize: "0.65rem" }} /> Lock
              </div>
              <div className="fw-bold text-warning">{money(lock)}</div>
            </div>
          </div>
          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <div className="text-muted small">Active ratio</div>
              <div className="small fw-semibold">{activePct.toFixed(0)}%</div>
            </div>
            <div className="progress">
              <motion.div
                className="progress-bar"
                initial={{ width: 0 }}
                animate={{ width: `${activePct}%` }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function TodayChip({ label, value, sub }) {
  return (
    <div className="border rounded-3 p-3 bg-white h-100">
      <div className="text-muted small text-uppercase fw-semibold" style={{ fontSize: "0.68rem", letterSpacing: "0.04em" }}>
        {label}
      </div>
      <div className="h5 fw-bold mb-0 mt-1 tb-display">{value}</div>
      {sub ? <div className="small text-muted mt-1">{sub}</div> : null}
    </div>
  );
}

function QueueLink({ to, label, count, loading }) {
  return (
    <Link
      to={to}
      className="text-decoration-none d-flex align-items-center justify-content-between gap-2 rounded-3 px-3 py-2 border bg-white"
      style={{ color: "inherit", transition: "border-color .18s ease, transform .18s ease" }}
    >
      <span className="fw-semibold small">{label}</span>
      {loading ? (
        <SkeletonLine w="28px" />
      ) : (
        <span className={`badge ${n(count) > 0 ? "text-bg-warning" : "text-bg-secondary"}`}>{nfmt(count)}</span>
      )}
    </Link>
  );
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [Data, setData] = useState(DEFAULT_DATA);
  const [lastUpdated, setLastUpdated] = useState(null);
  const { logout } = useAuth();

  const d = useMemo(() => {
    const src = Data || DEFAULT_DATA;
    return {
      users: src.users || DEFAULT_DATA.users,
      balance: {
        coin: { ...DEFAULT_DATA.balance.coin, ...(src.balance?.coin || {}) },
        usdt: { ...DEFAULT_DATA.balance.usdt, ...(src.balance?.usdt || {}) },
      },
      deposits: { ...DEFAULT_DATA.deposits, ...(src.deposits || {}) },
      withdrawals: { ...DEFAULT_DATA.withdrawals, ...(src.withdrawals || {}) },
      trades: { ...DEFAULT_DATA.trades, ...(src.trades || {}) },
      exchange: {
        buy: { ...DEFAULT_DATA.exchange.buy, ...(src.exchange?.buy || {}) },
        sell: { ...DEFAULT_DATA.exchange.sell, ...(src.exchange?.sell || {}) },
      },
      p2p: { ...DEFAULT_DATA.p2p, ...(src.p2p || {}) },
      commission: {
        ...DEFAULT_DATA.commission,
        ...(src.commission || {}),
        by_type: { ...DEFAULT_DATA.commission.by_type, ...(src.commission?.by_type || {}) },
      },
      ti_coin_buy: {
        ...DEFAULT_DATA.ti_coin_buy,
        ...(src.ti_coin_buy || {}),
        self: { ...DEFAULT_DATA.ti_coin_buy.self, ...(src.ti_coin_buy?.self || {}) },
        downline: { ...DEFAULT_DATA.ti_coin_buy.downline, ...(src.ti_coin_buy?.downline || {}) },
      },
      swaps: {
        usdt_to_coin: { ...DEFAULT_DATA.swaps.usdt_to_coin, ...(src.swaps?.usdt_to_coin || {}) },
        coin_to_usdt: { ...DEFAULT_DATA.swaps.coin_to_usdt, ...(src.swaps?.coin_to_usdt || {}) },
      },
      recovery: { ...DEFAULT_DATA.recovery, ...(src.recovery || {}) },
      today: {
        ...DEFAULT_DATA.today,
        ...(src.today || {}),
        deposits: { ...DEFAULT_DATA.today.deposits, ...(src.today?.deposits || {}) },
        withdrawals: { ...DEFAULT_DATA.today.withdrawals, ...(src.today?.withdrawals || {}) },
        trades: { ...DEFAULT_DATA.today.trades, ...(src.today?.trades || {}) },
      },
      pending_queue: { ...DEFAULT_DATA.pending_queue, ...(src.pending_queue || {}) },
    };
  }, [Data]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios({
        method: "post",
        url: `${BASE_ENDPOINT}/admin/stats`,
        maxBodyLength: Infinity,
        headers: { "Content-Type": "application/json" },
        data: { token: localStorage.getItem("token") },
      });

      const payload = response?.data;
      const err = payload?.error;

      if (err) {
        const msg = typeof err === "string" ? err : "Failed to fetch stats";
        if (msg.toLowerCase().includes("session expired")) {
          toast.error(msg);
          logout();
        } else {
          toast.error(msg);
        }
        return;
      }

      setData(payload?.data || DEFAULT_DATA);
      setLastUpdated(new Date());
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch stats");
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const lastUpdatedText = useMemo(() => {
    if (!lastUpdated) return "—";
    return lastUpdated.toLocaleString("en-IN");
  }, [lastUpdated]);

  const todayDateText = d.today?.date || "—";
  const queueTotal = n(d.pending_queue.total);

  return (
    <PageTransition>
      <div className="row g-3">
        {/* Header */}
        <div className="col-12">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 tb-page-header">
            <div>
              <h2 className="tb-page-title">Dashboard</h2>
              <div className="tb-page-subtitle">
                System overview · Last updated: <span className="fw-semibold text-dark">{lastUpdatedText}</span>
              </div>
            </div>
            <button
              className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
              onClick={fetchData}
              disabled={loading}
              type="button"
            >
              <FaSyncAlt className={loading ? "tb-spin" : ""} />
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {/* Pending queue */}
        <motion.div className="col-12" custom={0} variants={fadeUp} initial="hidden" animate="show">
          <div
            className="card border-0"
            style={{
              background: queueTotal > 0
                ? "linear-gradient(135deg, rgba(217,119,6,0.12), rgba(255,255,255,0.95))"
                : undefined,
            }}
          >
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="tb-panel-icon" style={{ background: TONES.amber.bg, color: TONES.amber.color }}>
                    <FaExclamationTriangle />
                  </span>
                  <div>
                    <div className="fw-semibold tb-display">Pending queue</div>
                    <div className="small text-muted">Items waiting for admin action</div>
                  </div>
                </div>
                {loading ? (
                  <span className="badge text-bg-secondary">Loading</span>
                ) : (
                  <span className={`badge ${queueTotal > 0 ? "text-bg-warning" : "text-bg-success"}`}>
                    {nfmt(queueTotal)} total
                  </span>
                )}
              </div>
              <div className="row g-2">
                <div className="col-12 col-sm-6 col-lg-3">
                  <QueueLink to="/deposits" label="Deposits" count={d.pending_queue.deposits} loading={loading} />
                </div>
                <div className="col-12 col-sm-6 col-lg-3">
                  <QueueLink to="/withdrawals" label="Withdrawals" count={d.pending_queue.withdrawals} loading={loading} />
                </div>
                <div className="col-12 col-sm-6 col-lg-3">
                  <QueueLink to="/p2p-transfer-list" label="Exchange Buy" count={d.pending_queue.exchange_buy} loading={loading} />
                </div>
                <div className="col-12 col-sm-6 col-lg-3">
                  <QueueLink to="/p2p-withdrawal-list" label="Exchange Sell" count={d.pending_queue.exchange_sell} loading={loading} />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Today strip */}
        <div className="col-12">
          <Panel
            title="Today"
            icon={<FaClock />}
            index={1}
            right={
              loading ? (
                <span className="badge text-bg-secondary">Loading</span>
              ) : (
                <span className="badge text-bg-primary">{todayDateText}</span>
              )
            }
          >
            {loading ? (
              <div className="row g-2">
                {[1, 2, 3, 4].map((i) => (
                  <div className="col-6 col-lg-3" key={i}>
                    <SkeletonLine w="100%" />
                    <div className="mt-2" />
                    <SkeletonLine w="60%" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="row g-2">
                <div className="col-6 col-lg-3">
                  <TodayChip label="New users" value={nfmt(d.today.new_users)} />
                </div>
                <div className="col-6 col-lg-3">
                  <TodayChip
                    label="Deposits"
                    value={money(d.today.deposits?.success?.amount)}
                    sub={`${nfmt(d.today.deposits?.success?.count)} success · ${nfmt(d.today.deposits?.pending?.count)} pending`}
                  />
                </div>
                <div className="col-6 col-lg-3">
                  <TodayChip
                    label="Withdrawals"
                    value={money(d.today.withdrawals?.success?.amount)}
                    sub={`${nfmt(d.today.withdrawals?.success?.count)} success · ${nfmt(d.today.withdrawals?.pending?.count)} pending`}
                  />
                </div>
                <div className="col-6 col-lg-3">
                  <TodayChip
                    label="Trades"
                    value={money(d.today.trades?.total?.amount)}
                    sub={`W ${nfmt(d.today.trades?.win?.count)} · L ${nfmt(d.today.trades?.loss?.count)}`}
                  />
                </div>
              </div>
            )}
          </Panel>
        </div>

        {/* Top KPIs */}
        <KpiTile loading={loading} icon={<FaUsers />} label="Total Users" value={nfmt(d.users.total)} hint={`${nfmt(d.users.active)} active`} index={2} tone="teal" />
        <KpiTile loading={loading} icon={<FaCoins />} label="Coin Balance" value={money(d.balance.coin.total)} hint={`Lock ${money(d.balance.coin.lock)}`} index={3} tone="amber" />
        <KpiTile loading={loading} icon={<FaDollarSign />} label="USDT Balance" value={money(d.balance.usdt.total)} hint={`Lock ${money(d.balance.usdt.lock)}`} index={4} tone="sky" />
        <KpiTile loading={loading} icon={<FaChartLine />} label="Trade Profit" value={money(d.trades.profit)} hint={`${nfmt(d.trades.total?.count)} trades`} index={5} tone="violet" />

        {/* Users + Balances */}
        <div className="col-12 col-lg-6">
          <Panel
            title="Users"
            icon={<FaChartPie />}
            index={6}
            right={
              !loading && (
                <span className="badge text-bg-primary">
                  Active {nfmt(d.users.active)} · Inactive {nfmt(d.users.inactive)}
                </span>
              )
            }
          >
            {loading ? (
              <>
                <SkeletonLine w="90%" />
                <div className="mt-2" />
                <SkeletonLine w="70%" />
              </>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th className="text-end">Count</th>
                      <th className="text-end">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-semibold">
                        <span className="me-2" style={{ color: "#059669" }}><FaUserCheck /></span>
                        Active
                      </td>
                      <td className="text-end">{nfmt(d.users.active)}</td>
                      <td className="text-end">{pct(d.users.active, d.users.total).toFixed(0)}%</td>
                    </tr>
                    <tr>
                      <td className="fw-semibold">
                        <span className="me-2" style={{ color: "#dc2626" }}><FaUserSlash /></span>
                        Inactive
                      </td>
                      <td className="text-end">{nfmt(d.users.inactive)}</td>
                      <td className="text-end">{pct(d.users.inactive, d.users.total).toFixed(0)}%</td>
                    </tr>
                    <tr>
                      <td className="fw-semibold">Total</td>
                      <td className="text-end fw-bold">{nfmt(d.users.total)}</td>
                      <td className="text-end">100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>

        <div className="col-12 col-lg-6">
          <Panel
            title="Balances"
            icon={<FaWallet />}
            index={7}
            right={!loading && <span className="badge text-bg-dark">Includes locked funds</span>}
          >
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <BalanceBlock
                  title="Coin"
                  icon={<FaCoins />}
                  total={d.balance.coin.total}
                  active={d.balance.coin.active}
                  inactive={d.balance.coin.inactive}
                  lock={d.balance.coin.lock}
                  loading={loading}
                  accent="#d97706"
                />
              </div>
              <div className="col-12 col-md-6">
                <BalanceBlock
                  title="USDT"
                  icon={<FaDollarSign />}
                  total={d.balance.usdt.total}
                  active={d.balance.usdt.active}
                  inactive={d.balance.usdt.inactive}
                  lock={d.balance.usdt.lock}
                  loading={loading}
                  accent="#0284c7"
                />
              </div>
            </div>
          </Panel>
        </div>

        {/* Deposits / Withdrawals / Trades */}
        <div className="col-12 col-lg-4">
          <Panel
            title="Deposits"
            icon={<FaArrowDown />}
            index={8}
            right={!loading && <span className="badge text-bg-success">{money(d.deposits.total?.amount)}</span>}
          >
            <StatusGrid
              loading={loading}
              rows={[
                { label: "Total", value: money(d.deposits.total?.amount), sub: `${nfmt(d.deposits.total?.count)} txns` },
                { label: "Pending", value: money(d.deposits.pending?.amount), sub: `${nfmt(d.deposits.pending?.count)} txns`, tone: "text-warning" },
                { label: "Success", value: money(d.deposits.success?.amount), sub: `${nfmt(d.deposits.success?.count)} txns`, tone: "text-success" },
                { label: "Rejected", value: money(d.deposits.rejected?.amount), sub: `${nfmt(d.deposits.rejected?.count)} txns`, tone: "text-danger" },
              ]}
            />
          </Panel>
        </div>

        <div className="col-12 col-lg-4">
          <Panel
            title="Withdrawals"
            icon={<FaArrowUp />}
            index={9}
            right={!loading && <span className="badge text-bg-dark">Net {money(d.withdrawals.total?.net)}</span>}
          >
            <StatusGrid
              loading={loading}
              rows={[
                { label: "Total", value: money(d.withdrawals.total?.amount), sub: `${nfmt(d.withdrawals.total?.count)} txns` },
                { label: "Pending", value: money(d.withdrawals.pending?.amount), sub: `${nfmt(d.withdrawals.pending?.count)} txns`, tone: "text-warning" },
                { label: "Success", value: money(d.withdrawals.success?.amount), sub: `${nfmt(d.withdrawals.success?.count)} txns`, tone: "text-success" },
                { label: "Rejected", value: money(d.withdrawals.rejected?.amount), sub: `${nfmt(d.withdrawals.rejected?.count)} txns`, tone: "text-danger" },
              ]}
            />
          </Panel>
        </div>

        <div className="col-12 col-lg-4">
          <Panel
            title="Trades"
            icon={<FaChartLine />}
            index={10}
            right={!loading && <span className="badge text-bg-primary">P/L {money(d.trades.profit)}</span>}
          >
            <StatusGrid
              loading={loading}
              rows={[
                { label: "Total", value: money(d.trades.total?.amount), sub: `${nfmt(d.trades.total?.count)} trades` },
                { label: "Pending", value: money(d.trades.pending?.amount), sub: `${nfmt(d.trades.pending?.count)} trades`, tone: "text-warning" },
                { label: "Win", value: money(d.trades.win?.amount), sub: `${nfmt(d.trades.win?.count)} trades`, tone: "text-success" },
                { label: "Loss", value: money(d.trades.loss?.amount), sub: `${nfmt(d.trades.loss?.count)} trades`, tone: "text-danger" },
              ]}
            />
          </Panel>
        </div>

        {/* Exchange */}
        <div className="col-12 col-lg-6">
          <Panel title="Exchange Buy" icon={<FaShoppingCart />} index={11}>
            <StatusGrid
              loading={loading}
              rows={[
                { label: "Total", value: `${money(d.exchange.buy.total?.usdt)} USDT`, sub: `${nfmt(d.exchange.buy.total?.count)} · ₹${money(d.exchange.buy.total?.inr)}` },
                { label: "Pending", value: `${money(d.exchange.buy.pending?.usdt)} USDT`, sub: `${nfmt(d.exchange.buy.pending?.count)} · ₹${money(d.exchange.buy.pending?.inr)}`, tone: "text-warning" },
                { label: "Success", value: `${money(d.exchange.buy.success?.usdt)} USDT`, sub: `${nfmt(d.exchange.buy.success?.count)} · ₹${money(d.exchange.buy.success?.inr)}`, tone: "text-success" },
                { label: "Rejected", value: `${money(d.exchange.buy.rejected?.usdt)} USDT`, sub: `${nfmt(d.exchange.buy.rejected?.count)} · ₹${money(d.exchange.buy.rejected?.inr)}`, tone: "text-danger" },
              ]}
            />
          </Panel>
        </div>

        <div className="col-12 col-lg-6">
          <Panel title="Exchange Sell" icon={<FaExchangeAlt />} index={12}>
            <StatusGrid
              loading={loading}
              rows={[
                { label: "Total", value: `${money(d.exchange.sell.total?.usdt)} USDT`, sub: `${nfmt(d.exchange.sell.total?.count)} · ₹${money(d.exchange.sell.total?.inr)}` },
                { label: "Pending", value: `${money(d.exchange.sell.pending?.usdt)} USDT`, sub: `${nfmt(d.exchange.sell.pending?.count)} · ₹${money(d.exchange.sell.pending?.inr)}`, tone: "text-warning" },
                { label: "Success", value: `${money(d.exchange.sell.success?.usdt)} USDT`, sub: `${nfmt(d.exchange.sell.success?.count)} · ₹${money(d.exchange.sell.success?.inr)}`, tone: "text-success" },
                { label: "Rejected", value: `${money(d.exchange.sell.rejected?.usdt)} USDT`, sub: `${nfmt(d.exchange.sell.rejected?.count)} · ₹${money(d.exchange.sell.rejected?.inr)}`, tone: "text-danger" },
              ]}
            />
          </Panel>
        </div>

        {/* P2P / Commission / TI Coin */}
        <div className="col-12 col-md-4">
          <Panel title="P2P" icon={<FaPeopleArrows />} index={13}>
            <StatusGrid
              loading={loading}
              rows={[
                { label: "Transfers", value: nfmt(d.p2p.count) },
                { label: "Amount", value: money(d.p2p.amount) },
              ]}
            />
          </Panel>
        </div>

        <div className="col-12 col-md-4">
          <Panel title="Commission" icon={<FaGift />} index={14}>
            <StatusGrid
              loading={loading}
              rows={[
                { label: "Total records", value: nfmt(d.commission.total_count) },
                { label: "Pending", value: money(d.commission.pending), tone: "text-warning" },
                { label: "Claimed", value: money(d.commission.claimed), tone: "text-success" },
                { label: "Refer", value: money(d.commission.by_type.refer) },
                { label: "Deposit", value: money(d.commission.by_type.deposit) },
                { label: "Self deposit", value: money(d.commission.by_type.self_deposit) },
                { label: "Trade", value: money(d.commission.by_type.trade) },
                { label: "TI coin buy", value: money(d.commission.by_type.ti_coin_buy) },
              ]}
            />
          </Panel>
        </div>

        <div className="col-12 col-md-4">
          <Panel
            title="TI Coin Buy"
            icon={<FaCoins />}
            index={15}
            right={!loading && <span className="badge text-bg-dark">{money(d.ti_coin_buy.total)}</span>}
          >
            <StatusGrid
              loading={loading}
              rows={[
                { label: "Self", value: money(d.ti_coin_buy.self?.amount), sub: `${nfmt(d.ti_coin_buy.self?.count)} buys` },
                { label: "Downline", value: money(d.ti_coin_buy.downline?.amount), sub: `${nfmt(d.ti_coin_buy.downline?.count)} buys` },
                { label: "Total amount", value: money(d.ti_coin_buy.total) },
              ]}
            />
          </Panel>
        </div>

        {/* Swaps / Recovery */}
        <div className="col-12 col-lg-6">
          <Panel title="Swaps" icon={<FaRandom />} index={16}>
            <StatusGrid
              loading={loading}
              rows={[
                {
                  label: "USDT → Coin",
                  value: `${money(d.swaps.usdt_to_coin.usdt)} USDT`,
                  sub: `${nfmt(d.swaps.usdt_to_coin.count)} · ${money(d.swaps.usdt_to_coin.coin)} coin`,
                },
                {
                  label: "Coin → USDT",
                  value: `${money(d.swaps.coin_to_usdt.coin)} coin`,
                  sub: `${nfmt(d.swaps.coin_to_usdt.count)} · ${money(d.swaps.coin_to_usdt.usdt)} USDT`,
                },
              ]}
            />
          </Panel>
        </div>

        <div className="col-12 col-lg-6">
          <Panel title="Recovery" icon={<FaHandHoldingUsd />} index={17}>
            <StatusGrid
              loading={loading}
              rows={[
                { label: "Total / Active", value: `${nfmt(d.recovery.total_count)} / ${nfmt(d.recovery.active_count)}` },
                { label: "Total amount", value: money(d.recovery.total_amount) },
                { label: "Pending", value: money(d.recovery.pending_amount), tone: "text-warning" },
                { label: "Claimed", value: money(d.recovery.claimed_amount), tone: "text-success" },
                { label: "Unclaimed", value: money(d.recovery.unclaimed_amount) },
              ]}
            />
          </Panel>
        </div>
      </div>
    </PageTransition>
  );
}
